// Serializer/Parser pro navigační stack <-> URL search params

import type { LayerDescriptor } from './layerDescriptor';
import type { StoredAgendaConfig } from './agendaDefinition';
import type { ParamCodecMap } from './paramCodec';
import type { LayerPresentation, LayerZone } from './navigationTypes';
import {
  namespacedKey,
  parseNamespacedKey,
  metaKey,
  parseMetaKey,
  META_KEYS,
} from './urlNamespace';

/** Serializovaná reprezentace jedné vrstvy z URL */
export interface UrlLayerDescriptor {
  readonly agendaId: string;
  readonly presentation: LayerPresentation;
  /** Identita floating stacku; undefined = plocha. */
  readonly stackId?: string;
  /** Zóna vrstvy v layoutu root agendy; undefined = běžná vrstva nad agendou. */
  readonly zone?: LayerZone;
  readonly rawOpenParams: Record<string, string>;
  readonly rawStateParams: Record<string, string>;
}

/**
 * Serializuje navigační stack do URL search params.
 * Každá vrstva dostane vlastní namespace l{index}_*.
 */
export function serializeStack(
  layers: readonly LayerDescriptor[],
  getConfig: (agendaId: string) => StoredAgendaConfig | undefined
): URLSearchParams {
  const searchParams = new URLSearchParams();

  layers.forEach((layer, index) => {
    // Meta: identita vrstvy
    searchParams.set(metaKey(index, META_KEYS.AGENDA_ID), layer.agendaId);
    searchParams.set(metaKey(index, META_KEYS.PRESENTATION), layer.presentation);
    // stackId emitujeme jen když je definovaný (plocha ho nemá)
    if (layer.stackId !== undefined) {
      searchParams.set(metaKey(index, META_KEYS.STACK_ID), layer.stackId);
    }
    // zone emitujeme jen když je definovaná (běžná vrstva ji nemá)
    if (layer.zone !== undefined) {
      searchParams.set(metaKey(index, META_KEYS.ZONE), layer.zone);
    }

    const config = getConfig(layer.agendaId);
    if (!config) return;

    // Serializace open parametrů
    encodeParams(searchParams, index, config.open, layer.openParams, 'o');

    // Serializace state parametrů
    if (config.state) {
      encodeParams(searchParams, index, config.state, layer.stateParams, 's');
    }
  });

  return searchParams;
}

/**
 * Parsuje URL search params na seznam surových layer descriptorů.
 * Vrací surové stringy – dekódování proti registru provádí restore pipeline.
 */
export function parseStack(searchParams: URLSearchParams): UrlLayerDescriptor[] {
  // Najdi všechny vrstvy podle meta klíčů
  const layerMap = new Map<
    number,
    { agendaId?: string; presentation?: string; stackId?: string; zone?: LayerZone }
  >();
  const paramMap = new Map<
    number,
    { open: Record<string, string>; state: Record<string, string> }
  >();

  for (const [key, value] of searchParams.entries()) {
    // Zkus parsovat jako meta klíč
    const meta = parseMetaKey(key);
    if (meta) {
      const entry = layerMap.get(meta.layerIndex) ?? {};
      if (meta.metaName === META_KEYS.AGENDA_ID) entry.agendaId = value;
      if (meta.metaName === META_KEYS.PRESENTATION) entry.presentation = value;
      if (meta.metaName === META_KEYS.STACK_ID) entry.stackId = value;
      // Neznámou hodnotu zóny tiše ignorujeme – vrstva se obnoví jako běžná.
      if (meta.metaName === META_KEYS.ZONE && value === 'detail') entry.zone = value;
      layerMap.set(meta.layerIndex, entry);
      continue;
    }

    // Zkus parsovat jako namespacovaný parametr
    const param = parseNamespacedKey(key);
    if (param) {
      const entry = paramMap.get(param.layerIndex) ?? { open: {}, state: {} };
      if (param.paramName.startsWith('o_')) {
        entry.open[param.paramName.slice(2)] = value;
      } else if (param.paramName.startsWith('s_')) {
        entry.state[param.paramName.slice(2)] = value;
      }
      paramMap.set(param.layerIndex, entry);
    }
  }

  // Seřaď vrstvy podle indexu
  const sortedIndices = [...layerMap.keys()].sort((a, b) => a - b);

  return sortedIndices
    .map((index) => {
      const meta = layerMap.get(index);
      if (!meta?.agendaId || !meta.presentation) return null;

      const params = paramMap.get(index) ?? { open: {}, state: {} };

      return {
        agendaId: meta.agendaId,
        presentation: meta.presentation as LayerPresentation,
        ...(meta.stackId !== undefined ? { stackId: meta.stackId } : {}),
        ...(meta.zone !== undefined ? { zone: meta.zone } : {}),
        rawOpenParams: params.open,
        rawStateParams: params.state,
      };
    })
    .filter((d): d is UrlLayerDescriptor => d !== null);
}

/** Enkóduje parametry jedné skupiny do search params */
function encodeParams(
  searchParams: URLSearchParams,
  layerIndex: number,
  codecMap: ParamCodecMap,
  values: Record<string, unknown>,
  prefix: 'o' | 's'
): void {
  for (const [name, def] of Object.entries(codecMap)) {
    const value = values[name];
    if (value === undefined) continue;

    try {
      const encoded = def.codec.encode(value);
      searchParams.set(namespacedKey(layerIndex, `${prefix}_${name}`), encoded);
    } catch {
      // Tiché přeskočení neenkódovatelných hodnot
    }
  }
}
